import Button from "@src/components/Button/Button";
import FriendRequest from "@src/components/FriendRequests/FriendRequests";
import Loader from "@src/components/Loader/Loader";
import Modal from "@src/components/Modal/Modal";
import SearchInput from "@src/components/SearchInput/SearchInput";
import {
    Block,
    Friend,
    FriendRequest as FriendRequestType,
    User,
} from "@src/types/global";
import { api } from "@src/utils/axios-instance";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
type Item = {
    id: string;
    name: string;
};
/*
 * Send friend requests, view pending requests (out of notification box), manage blocked users, etc
 */
const UserRelationshipManager = () => {
    const [searchResults, setSearchResults] = useState<User[]>([]);
    const [selectedItem, setSelectedItem] = useState<User | undefined>(
        undefined,
    );
    const [displayResults, setDisplayResults] = useState<Item[]>([]);
    const [value, setValue] = useState("");
    const [open, setOpen] = useState(false);

    const queryClient = useQueryClient();
    // Get friends and display
    const fetchFriends = async (): Promise<AxiosResponse<{ data: Friend[] }>> =>
        await api.get("/friends/");
    // Get blocks and display
    const fetchBlocks = async (): Promise<AxiosResponse<{ data: Block[] }>> =>
        await api.get("/blocks/");

    async function fetchByUserName(name: string): Promise<User[]> {
        const { data } = await api.get("users/?username=" + name);
        return data.data;
    }
    async function addFriend(id: string) {
        await api.post("/friends/", { request: { user_id: id } });
    }
    async function removeFriend(id: string) {
        await api.delete("/friends/" + id);
    }
    async function blockUser(id: string) {
        await api.post("/blocks/", { block: { blocked_user_id: id } });
    }
    async function unblockUser(block_id: string) {
        await api.delete("/blocks/" + block_id);
    }
    async function fetchPendingRequests(): Promise<FriendRequestType[]> {
        const { data } = await api.get("/friends/requests/");
        return data.data;
    }

    const friendsQuery = useQuery({
        queryFn: fetchFriends,
        queryKey: ["friends"],
        select: (response) => response.data.data,
    });
    const blocksQuery = useQuery({
        queryFn: fetchBlocks,
        queryKey: ["blocks"],
        select: (response) => response.data.data,
    });
    const pendingRequests = useQuery({
        queryFn: fetchPendingRequests,
        queryKey: ["friendRequests"],
    });
    const userQuery = useQuery({
        queryKey: ["username", value],
        queryFn: () => fetchByUserName(value),
        enabled: false,
    });

    const addFriendMutation = useMutation({
        mutationFn: addFriend,
        onSuccess: () => {
            toast.success("Request sent");
            setOpen(false);
        },
        onError: () => toast.error("Could not send request"),
    });

    const removeFriendMutation = useMutation({
        mutationFn: removeFriend,
        onSuccess: () => {
            toast.success("Removed user from friendlist");
            queryClient.invalidateQueries({ queryKey: ["friends"] });
        },
        onError: () => {
            toast.error("Could not unfriend user, try again later.");
        },
    });
    const blockUserMutation = useMutation({
        mutationFn: blockUser,
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ["blocks"] });
            toast.success(
                "User blocked. If you need more assistance, reach out to the admin.",
                {
                    duration: 10000,
                },
            );
            setOpen(false);
        },
        onError: () => {
            toast.error("Could not block user");
        },
    });
    const unblockUserMutation = useMutation({
        mutationFn: unblockUser,
        onSuccess: () => {
            toast.success("User unblocked. ");
            queryClient.invalidateQueries({ queryKey: ["blocks"] });
        },
        onError: () => {
            toast.error("Could not unblock user, try again later.");
        },
    });

    async function parseData() {
        if (value !== "") {
            await userQuery.refetch().then((res) => {
                console.log(res.data);
                if (res.data && res.data.length > 0) {
                    transformSearchResults(res.data!);
                    setSearchResults(res.data!);
                } else {
                    setDisplayResults([]);
                    setSearchResults([]);
                }
            });
        } else {
            setDisplayResults([]);
            setSearchResults([]);
        }
    }

    /**
     * Transforms the array of given user to an array of {name: string, id: string} objects
     * for display in the searchResults
     * @param {User[]} - users - The array of users coming from the server to convert
     */
    function transformSearchResults(users: User[]) {
        if (users && users.length) {
            const usersArray = users.map((user) => {
                return {
                    name: user.username,
                    id: user.id,
                };
            });
            setDisplayResults(usersArray);
        }
    }
    /**
     * The function to call whenever a user types in the box
     * @param {string} - value - The user input
     */
    const handleChange = async (value: string) => {
        setValue(value);
    };

    /**
     * Looks for the given item's ID in the search results and sets it as the selected item
     * @param item - the clicked item
     */
    function pickUser(item: Item) {
        setSelectedItem(searchResults.find((el) => el.id == item.id));
        setOpen(true);
    }

    useEffect(() => {
        parseData();
    }, [value]);
    return (
        <section>
            <h2>User relationship manager</h2>
            <SearchInput
                searchResults={displayResults}
                onChange={handleChange}
                isLoading={userQuery.isFetching}
                placeholder="Enter a username"
                name="add-friend"
                onClick={pickUser}
                onClickOutside={() => setDisplayResults([])}
            />
            <div>
                <h3>Friendlist</h3>
                {friendsQuery.isPending ? (
                    <Loader />
                ) : (
                    friendsQuery.data &&
                    friendsQuery.data.map((data) => (
                        <div key={data.id}>
                            {data.friend.username}{" "}
                            <Button
                                type="button"
                                preset="stealth"
                                fn={() => removeFriendMutation.mutate(data.id)}
                            >
                                Remove friend
                            </Button>
                        </div>
                    ))
                )}
                <h4>Pending requests :</h4>
                {pendingRequests.isPending ? (
                    <Loader />
                ) : pendingRequests.isSuccess &&
                  pendingRequests.data.length > 0 ? (
                    pendingRequests.data.map((request) => (
                        <FriendRequest request={request} key={request.id} />
                    ))
                ) : (
                    <p>No pending request</p>
                )}

                <h3>Blocklist</h3>
                {blocksQuery.isPending ? (
                    <Loader />
                ) : (
                    blocksQuery.data &&
                    blocksQuery.data.map((data) => (
                        <p key={data.id}>
                            {data.blocked_user.username}
                            <Button
                                type="button"
                                preset="stealth"
                                fn={() => unblockUserMutation.mutate(data.id)}
                            >
                                Unblock
                            </Button>
                        </p>
                    ))
                )}
            </div>
            <Modal
                isOpen={open}
                closeModalFn={() => {
                    setOpen(false);
                    setSelectedItem(undefined);
                }}
            >
                <h3 style={{ textAlign: "center" }}>
                    {selectedItem?.username} : Friend or foe ?
                </h3>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        justifyContent: "space-between",
                    }}
                >
                    <Button
                        preset="primary"
                        fn={() => addFriendMutation.mutate(selectedItem!.id)}
                    >
                        Add friend
                    </Button>
                    <Button
                        preset="danger"
                        fn={() => blockUserMutation.mutate(selectedItem!.id)}
                    >
                        Block
                    </Button>
                </div>
            </Modal>
        </section>
    );
};
export default UserRelationshipManager;
